import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Upload,
  XCircle,
  ArrowLeft,
  ShieldCheck,
  ShieldAlert,
  Check,
  CircleDot,
  Languages,
  Pencil,
  Trash2,
} from "lucide-react";
import { Btn } from "../../components/Btn";
import { Modal } from "../../components/Modal";
import { Sparkline } from "../../components/Sparkline";
import { useToast } from "../../components/Toast";
import { StatusChip } from "../../components/dashboardModals";
import {
  applications,
  versionsFor,
  COMMON_PERMISSIONS,
  COMMON_LOCALES,
  type AppVersion,
  type Application,
} from "../../data";
import {
  createAppLocale,
  deleteAppLocale,
  getApp,
  publishVersion,
  revokeVersion,
  updateAppLocale,
} from "../../api/endpoints";
import { toAppVersion, toApplication } from "../../api/adapters";
import { ApiError, type AppLocaleCreate, type AppLocaleOut } from "../../api/types";

type TabId = "overview" | "versions" | "permissions" | "locales" | "stats";

export default function AppPage() {
  const { pkg = "" } = useParams();
  const detailQ = useQuery({
    queryKey: ["app", pkg],
    queryFn: () => getApp(pkg),
    enabled: !!pkg,
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 1;
    },
  });

  // Fallback на mock если API недоступен. 404 от бэка обрабатываем отдельно (NotFound).
  const apiApp: Application | undefined = detailQ.data ? toApplication(detailQ.data) : undefined;
  const apiVersions: AppVersion[] | undefined = detailQ.data?.versions.map(toAppVersion);
  const locales: AppLocaleOut[] = detailQ.data?.locales ?? COMMON_LOCALES.map((locale) => ({
    ...locale,
    code: locale.code === "kz" ? "kk" : locale.code,
    title: locale.name,
    short_description: null,
  }));

  const mockApp = useMemo(() => applications.find((a) => a.pkg === pkg), [pkg]);
  const mockVersions = useMemo(() => versionsFor(pkg), [pkg]);

  const isNotFound =
    detailQ.isError && detailQ.error instanceof ApiError && detailQ.error.status === 404;

  // Приоритет: реальные данные → mock-карточка (на loading / network-ошибке) → NotFound
  const app: Application | undefined = apiApp ?? mockApp;
  const versions: AppVersion[] = apiVersions ?? mockVersions;

  const [tab, setTab] = useState<TabId>("versions");
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  if (isNotFound && !apiApp) return <NotFound pkg={pkg} />;
  if (!app) return <NotFound pkg={pkg} />;

  return (
    <>
      <BreadCrumb pkg={pkg} />
      <AppHeader
        app={app}
        onUpload={() => setUploadOpen(true)}
        onRevoke={() => setRevokeTarget(app.version)}
      />
      <Tabs tab={tab} onChange={setTab} />

      <div className="mt-4">
        {tab === "overview" && <OverviewTab app={app} versions={versions} />}
        {tab === "versions" && (
          <VersionsTab app={app} versions={versions} onRevoke={setRevokeTarget} />
        )}
        {tab === "permissions" && <PermissionsTab />}
        {tab === "locales" && <LocalesTab pkg={pkg} locales={locales} />}
        {tab === "stats" && <StatsTab app={app} />}
      </div>

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} app={app} />
      <RevokeModal
        open={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        app={app}
        version={revokeTarget ?? app.version}
      />
    </>
  );
}

// ───────────────────── шапка карточки ─────────────────────

function BreadCrumb({ pkg }: { pkg: string }) {
  return (
    <Link
      to="/admin/apps"
      className="mb-2 inline-flex items-center gap-1.5 text-[12.5px] text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-fg)]"
    >
      <ArrowLeft size={13} />
      <span>Каталог</span>
      <span className="px-1 text-[var(--color-fg-faint)]">/</span>
      <span className="font-mono">{pkg}</span>
    </Link>
  );
}

function AppHeader({
  app,
  onUpload,
  onRevoke,
}: {
  app: Application;
  onUpload: () => void;
  onRevoke: () => void;
}) {
  const revoked = app.status === "revoked";
  return (
    <header className="flex flex-col gap-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 sm:flex-row sm:items-start sm:gap-5 sm:p-5">
      <span
        className="grid shrink-0 place-items-center rounded-2xl font-semibold text-white"
        style={{ background: app.iconColor, width: 64, height: 64, fontSize: 28 }}
      >
        {app.iconLetter}
      </span>
      <div className="min-w-0 flex-1">
        <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)]">
          {app.name}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="font-mono text-[var(--color-fg-muted)]">{app.pkg}</span>
          <Dot />
          <StatusChip status={app.status} />
          <Dot />
          <span className="font-mono text-[var(--color-fg-muted)]">v. {app.version}</span>
          <Dot />
          <span className="font-mono text-[var(--color-fg-muted)]">поколение {app.generation}</span>
        </div>
        <p className="mt-2 text-[13px] text-[var(--color-fg-muted)]">
          {app.shortDescription} Группа доступа:{" "}
          <span className="font-mono text-[var(--color-fg)]">{app.visibility}</span>.
        </p>
        {revoked && app.revokedReason && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-[var(--color-danger)]/30 bg-[var(--color-danger-bg)] px-3 py-2 text-[12px] text-[var(--color-danger)]">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <span>
              Версия {app.version} отозвана: <span className="font-mono">{app.revokedReason}</span>. Установленным устройствам отправлено уведомление об обновлении.
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 sm:flex-col sm:gap-2">
        <Btn icon={Upload} onClick={onUpload}>Загрузить APK</Btn>
        {!revoked && (
          <Btn icon={XCircle} variant="danger" onClick={onRevoke}>
            Отозвать версию
          </Btn>
        )}
      </div>
    </header>
  );
}

function Dot() {
  return <span className="text-[var(--color-fg-faint)]">·</span>;
}

// ───────────────────── табы ─────────────────────

function Tabs({ tab, onChange }: { tab: TabId; onChange: (t: TabId) => void }) {
  const items: { id: TabId; label: string }[] = [
    { id: "overview",    label: "Обзор" },
    { id: "versions",    label: "Версии" },
    { id: "permissions", label: "Разрешения" },
    { id: "locales",     label: "Локализации" },
    { id: "stats",       label: "Статистика" },
  ];
  return (
    <div className="mt-5 -mx-1 overflow-x-auto">
      <div className="flex min-w-max gap-1 border-b border-[var(--color-border)] px-1">
        {items.map((it) => {
          const active = tab === it.id;
          return (
            <button
              key={it.id}
              onClick={() => onChange(it.id)}
              className={
                "relative -mb-px px-3 py-2 text-[13px] transition-colors " +
                (active
                  ? "font-medium text-[var(--color-fg)]"
                  : "text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]")
              }
            >
              {it.label}
              {active && (
                <span className="absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-[var(--color-brand)]" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────── контент табов ─────────────────────

function VersionsTab({
  app,
  versions,
  onRevoke,
}: {
  app: Application;
  versions: AppVersion[];
  onRevoke: (version: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 lg:col-span-2">
        <div>
          <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
            История версий
          </h2>
          <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
            Хранится 5 предыдущих версий + дельты
          </p>
        </div>
        <div className="-mx-1 mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                <Th>Версия</Th>
                <Th>Поколение</Th>
                <Th>Что нового</Th>
                <Th>Размер</Th>
                <Th>Время</Th>
                <Th>Статус</Th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.version} className="border-t border-[var(--color-border)]">
                  <td className="whitespace-nowrap px-3 py-3 font-mono text-[13px] font-medium">{v.version}</td>
                  <td className="whitespace-nowrap px-3 py-3 font-mono text-[var(--color-fg-muted)]">g.{v.generation}</td>
                  <td className="px-3 py-3 text-[var(--color-fg-muted)]">{v.changelog}</td>
                  <td className="whitespace-nowrap px-3 py-3 font-mono text-[var(--color-fg-muted)]">{v.sizeMb} MB</td>
                  <td className="whitespace-nowrap px-3 py-3 text-[var(--color-fg-muted)]">{v.date}</td>
                  <td className="whitespace-nowrap px-3 py-3">
                    {v.status === "current" && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-success-bg)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--color-success)]">
                        <CircleDot size={10} /> текущая
                      </span>
                    )}
                    {v.status === "revoked" && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-danger-bg)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--color-danger)]">
                        <ShieldAlert size={10} /> отозвана
                      </span>
                    )}
                    {v.status === "previous" && (
                      <button
                        type="button"
                        onClick={() => onRevoke(v.version)}
                        className="text-[12px] text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-danger)]"
                      >
                        отозвать
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="flex flex-col gap-3">
        <SidePanelSign app={app} />
        <SidePanelDownloads app={app} />
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function SidePanelSign({ app }: { app: Application }) {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <h3 className="flex items-center gap-2 text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
        <ShieldCheck size={15} className="text-[var(--color-success)]" />
        Подпись и распространение
      </h3>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[12px]">
        <dt className="text-[var(--color-fg-muted)]">Корп. подпись</dt>
        <dd className="text-right font-mono text-[var(--color-fg)]">SHA-256 ✓</dd>
        <dt className="text-[var(--color-fg-muted)]">Доставлено на</dt>
        <dd className="text-right font-mono text-[var(--color-fg)]">14 / 14 локалов</dd>
        <dt className="text-[var(--color-fg-muted)]">Установлено</dt>
        <dd className="text-right font-mono text-[var(--color-fg)]">{app.installs}</dd>
        <dt className="text-[var(--color-fg-muted)]">Дельты</dt>
        <dd className="text-right font-mono text-[var(--color-fg)]">5 шт.</dd>
      </dl>
    </section>
  );
}

function SidePanelDownloads({ app }: { app: Application }) {
  const values = useMemo(
    () => Array.from({ length: 30 }, (_, i) => 100 + Math.round(Math.sin(i / 3) * 30) + i * 4),
    [],
  );
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <h3 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
        Скачивания · 30 дней
      </h3>
      <Sparkline values={values} color="var(--color-brand-2)" className="mt-3 h-12 w-full" />
      <div className="mt-2 flex items-baseline justify-between">
        <span className="text-[20px] font-semibold tracking-[-0.02em] text-[var(--color-fg)]">
          {app.downloads30d.toLocaleString("ru-RU").replace(/\s/g, " ")}
        </span>
        <span className="text-[11.5px] text-[var(--color-fg-muted)]">скачиваний</span>
      </div>
    </section>
  );
}

function OverviewTab({ app, versions }: { app: Application; versions: AppVersion[] }) {
  const latest = versions[0];
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 lg:col-span-2">
        <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">О приложении</h2>
        <p className="mt-2 text-[13px] text-[var(--color-fg-muted)]">{app.shortDescription}</p>
        <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 text-[12.5px] sm:grid-cols-2">
          <KvRow label="Пакет"        value={<span className="font-mono">{app.pkg}</span>} />
          <KvRow label="Текущая версия" value={<span className="font-mono">{app.version} · g.{app.generation}</span>} />
          <KvRow label="Размер"        value={<span className="font-mono">{app.sizeMb} MB</span>} />
          <KvRow label="Разработчик"   value={<span className="font-mono">{app.developer}</span>} />
          <KvRow label="Видимость"     value={<span className="font-mono">{app.visibility}</span>} />
          <KvRow label="Категория"     value={app.category} />
        </dl>
      </section>
      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Последняя публикация</h2>
        <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 text-[12.5px]">
          {latest ? (
            <>
              <div className="font-mono font-medium text-[var(--color-fg)]">
                {latest.version} <span className="text-[var(--color-fg-muted)]">· g.{latest.generation}</span>
              </div>
              <div className="mt-1 text-[var(--color-fg-muted)]">{latest.changelog}</div>
              <div className="mt-2 font-mono text-[11px] text-[var(--color-fg-faint)]">{latest.date}</div>
            </>
          ) : (
            <div className="text-[var(--color-fg-muted)]">Версии ещё не опубликованы.</div>
          )}
        </div>
      </section>
    </div>
  );
}

function KvRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">{label}</div>
      <div className="mt-0.5 text-[var(--color-fg)]">{value}</div>
    </div>
  );
}

function PermissionsTab() {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Разрешения из манифеста</h2>
          <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
            Извлекаются автоматически при загрузке APK. Изменения требуют новой версии.
          </p>
        </div>
      </div>
      <ul className="mt-4 divide-y divide-[var(--color-border)]">
        {COMMON_PERMISSIONS.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-3 py-2.5 text-[12.5px]">
            <div className="min-w-0">
              <div className="font-medium text-[var(--color-fg)]">{p.name}</div>
              <div className="truncate font-mono text-[11px] text-[var(--color-fg-muted)]">{p.id}</div>
            </div>
            <span
              className={
                "shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-medium " +
                (p.level === "dangerous"
                  ? "bg-[var(--color-warning-bg)] text-[var(--color-warning)]"
                  : "bg-[var(--color-success-bg)] text-[var(--color-success)]")
              }
            >
              {p.level === "dangerous" ? "опасное" : "обычное"}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const AVAILABLE_LOCALES = [
  { code: "ru", name: "Русский" },
  { code: "en", name: "English" },
  { code: "tt", name: "Татарский" },
  { code: "kk", name: "Қазақша" },
  { code: "de", name: "Deutsch" },
  { code: "fr", name: "Français" },
  { code: "es", name: "Español" },
  { code: "it", name: "Italiano" },
  { code: "zh-CN", name: "简体中文" },
  { code: "uz", name: "O‘zbekcha" },
];

function LocalesTab({ pkg, locales }: { pkg: string; locales: AppLocaleOut[] }) {
  const qc = useQueryClient();
  const push = useToast();
  const [editing, setEditing] = useState<AppLocaleOut | "new" | null>(null);
  const deleteMut = useMutation({
    mutationFn: (code: string) => deleteAppLocale(pkg, code),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["app", pkg] });
      push({ kind: "success", text: "Локализация удалена" });
    },
    onError: (e) => push({
      kind: "warn",
      text: e instanceof ApiError ? e.message : "Не удалось удалить локализацию",
    }),
  });

  const remove = (locale: AppLocaleOut) => {
    if (window.confirm(`Удалить локализацию «${locale.name}»?`)) {
      deleteMut.mutate(locale.code);
    }
  };

  return (
    <>
      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Локализации</h2>
            <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
              Название и описание приложения для каждого языка каталога.
            </p>
          </div>
          <Btn icon={Languages} variant="ghost" onClick={() => setEditing("new")}>
            Добавить язык
          </Btn>
        </div>
        <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {locales.map((l) => (
          <li
            key={l.code}
            className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-border)] px-3 py-2.5"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-[var(--color-surface-3)] font-mono text-[11px] font-medium uppercase text-[var(--color-fg)]">
                {l.code}
              </span>
              <div>
                <div className="text-[13px] font-medium text-[var(--color-fg)]">{l.name}</div>
                <div className="text-[11px] text-[var(--color-fg-muted)]">
                  {l.complete ? "100% переведено" : "перевод не завершён"}
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <span className={
                "mr-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium " +
                (l.reviewed
                  ? "bg-[var(--color-success-bg)] text-[var(--color-success)]"
                  : "bg-[var(--color-warning-bg)] text-[var(--color-warning)]")
              }>
                {l.reviewed && <Check size={10} className="mr-1 inline" />}
                {l.reviewed ? "проверено" : "требует ревью"}
              </span>
              <button
                type="button"
                onClick={() => setEditing(l)}
                aria-label={`Редактировать ${l.name}`}
                className="rounded-md p-1.5 text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-fg)]"
              >
                <Pencil size={13} />
              </button>
              <button
                type="button"
                onClick={() => remove(l)}
                aria-label={`Удалить ${l.name}`}
                className="rounded-md p-1.5 text-[var(--color-fg-muted)] hover:bg-[var(--color-danger-bg)] hover:text-[var(--color-danger)]"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </li>
          ))}
          {!locales.length && (
            <li className="rounded-lg border border-dashed border-[var(--color-border-strong)] px-4 py-8 text-center text-[12.5px] text-[var(--color-fg-muted)] sm:col-span-2">
              Локализаций пока нет. Добавьте первый язык карточки.
            </li>
          )}
        </ul>
      </section>
      <LocaleModal
        open={editing !== null}
        pkg={pkg}
        locale={editing === "new" ? null : editing}
        existingCodes={locales.map((locale) => locale.code)}
        onClose={() => setEditing(null)}
      />
    </>
  );
}

function LocaleModal({
  open,
  pkg,
  locale,
  existingCodes,
  onClose,
}: {
  open: boolean;
  pkg: string;
  locale: AppLocaleOut | null;
  existingCodes: string[];
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={locale ? `Локализация: ${locale.name}` : "Добавить язык"}
      subtitle="Данные карточки приложения в выбранном языке"
      size="sm"
    >
      {open && (
        <LocaleForm
          pkg={pkg}
          locale={locale}
          existingCodes={existingCodes}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}

function LocaleForm({
  pkg,
  locale,
  existingCodes,
  onClose,
}: {
  pkg: string;
  locale: AppLocaleOut | null;
  existingCodes: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const push = useToast();
  const choices = AVAILABLE_LOCALES.filter(
    (item) => item.code === locale?.code || !existingCodes.includes(item.code),
  );
  const initial = locale ?? choices[0] ?? AVAILABLE_LOCALES[0];
  const [code, setCode] = useState(locale?.code ?? initial.code);
  const selected = AVAILABLE_LOCALES.find((item) => item.code === code) ?? initial;
  const [title, setTitle] = useState(locale?.title ?? "");
  const [description, setDescription] = useState(locale?.short_description ?? "");
  const [complete, setComplete] = useState(locale?.complete ?? false);
  const [reviewed, setReviewed] = useState(locale?.reviewed ?? false);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: AppLocaleCreate = {
        code,
        name: selected.name,
        title: title.trim(),
        short_description: description.trim() || null,
        complete,
        reviewed,
      };
      return locale
        ? updateAppLocale(pkg, locale.code, payload)
        : createAppLocale(pkg, payload);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["app", pkg] });
      push({ kind: "success", text: locale ? "Локализация обновлена" : "Язык добавлен" });
      onClose();
    },
    onError: (e) => push({
      kind: "warn",
      text: e instanceof ApiError ? e.message : "Не удалось сохранить локализацию",
    }),
  });
  const canSave = title.trim().length > 0 && !mutation.isPending;

  return (
    <div className="space-y-3 text-[12.5px]">
      <FieldEdit label="Язык">
        <select
          value={code}
          onChange={(event) => setCode(event.target.value)}
          disabled={!!locale || mutation.isPending}
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
        >
          {choices.map((item) => (
            <option key={item.code} value={item.code}>{item.name} · {item.code}</option>
          ))}
        </select>
      </FieldEdit>
      <FieldEdit label="Название приложения">
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          disabled={mutation.isPending}
          placeholder="Название в выбранном языке"
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
        />
      </FieldEdit>
      <FieldEdit label="Краткое описание">
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          disabled={mutation.isPending}
          rows={3}
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
        />
      </FieldEdit>
      <label className="flex items-center gap-2 text-[var(--color-fg)]">
        <input type="checkbox" checked={complete} onChange={(e) => setComplete(e.target.checked)} />
        Перевод заполнен полностью
      </label>
      <label className="flex items-center gap-2 text-[var(--color-fg)]">
        <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />
        Перевод проверен
      </label>
      <div className="flex justify-end gap-2 pt-2">
        <Btn onClick={onClose}>Отмена</Btn>
        <Btn variant="primary" onClick={() => canSave && mutation.mutate()}>
          {mutation.isPending ? "Сохранение…" : "Сохранить"}
        </Btn>
      </div>
    </div>
  );
}

function StatsTab({ app }: { app: Application }) {
  const values = useMemo(
    () => Array.from({ length: 30 }, (_, i) => 80 + Math.round(Math.sin(i / 3) * 40) + i * 6),
    [],
  );
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
      <Stat label="Установок" value={app.installs.toLocaleString("ru-RU")} />
      <Stat label="Скачиваний · 30 дн" value={app.downloads30d.toLocaleString("ru-RU")} />
      <Stat label="Размер APK" value={`${app.sizeMb} MB`} />
      <Stat label="Поколение" value={`g.${app.generation}`} />
      <section className="md:col-span-2 xl:col-span-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Скачивания · 30 дней</h2>
        <Sparkline values={values} color="var(--color-brand-2)" className="mt-3 h-24 w-full" />
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">{label}</div>
      <div className="mt-2 text-[24px] font-semibold tracking-[-0.02em] text-[var(--color-fg)]">{value}</div>
    </div>
  );
}

// ───────────────────── модалки ─────────────────────

function UploadModal({
  open,
  onClose,
  app,
}: {
  open: boolean;
  onClose: () => void;
  app: Application;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Публикация новой версии"
      subtitle={`Приложение ${app.name} · ${app.pkg}`}
      size="md"
    >
      {/* Внутренняя форма размонтируется при закрытии — это сбрасывает state без useEffect. */}
      {open && <UploadForm app={app} onClose={onClose} />}
    </Modal>
  );
}

function UploadForm({ app, onClose }: { app: Application; onClose: () => void }) {
  const push = useToast();
  const qc = useQueryClient();

  // Дефолтные значения: bump patch, generation +1.
  const nextVersion = useMemo(() => bumpPatch(app.version), [app.version]);
  const nextGen = (app.generation || 0) + 1;

  const [version, setVersion] = useState(nextVersion);
  const [generation, setGeneration] = useState(String(nextGen));
  const [sizeMb, setSizeMb] = useState(String(app.sizeMb || 0));
  const [changelog, setChangelog] = useState("");

  const publishMut = useMutation({
    mutationFn: () =>
      publishVersion(app.pkg, {
        version: version.trim(),
        generation: Number(generation) || 0,
        changelog: changelog.trim() || null,
        size_mb: Number(sizeMb) || 0,
      }),
    onSuccess: async (ver) => {
      await qc.invalidateQueries({ queryKey: ["app", app.pkg] });
      push({ kind: "success", text: `Версия ${ver.version} опубликована` });
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Не удалось опубликовать версию") : "Не удалось опубликовать версию";
      push({ kind: "warn", text: msg });
    },
  });

  return (
    <>
      <div className="text-[13px] text-[var(--color-fg-muted)]">
        Регистрирует новую версию в каталоге. APK можно загрузить отдельно из кабинета
        разработчика — там пройдут проверки и подпись.
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <FieldEdit label="Версия">
          <input
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            disabled={publishMut.isPending}
            placeholder="4.2.0"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </FieldEdit>
        <FieldEdit label="Поколение">
          <input
            value={generation}
            onChange={(e) => setGeneration(e.target.value)}
            disabled={publishMut.isPending}
            type="number"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </FieldEdit>
        <FieldEdit label="Размер, MB">
          <input
            value={sizeMb}
            onChange={(e) => setSizeMb(e.target.value)}
            disabled={publishMut.isPending}
            type="number"
            step="0.1"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </FieldEdit>
      </div>

      <label className="mt-4 block text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
        Что нового
      </label>
      <textarea
        value={changelog}
        onChange={(e) => setChangelog(e.target.value)}
        disabled={publishMut.isPending}
        rows={4}
        placeholder="- Исправлена ошибка X"
        className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
      />

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Btn onClick={onClose}>Отмена</Btn>
        <Btn variant="primary" onClick={() => publishMut.mutate()}>
          {publishMut.isPending ? "Публикация…" : "Опубликовать"}
        </Btn>
      </div>
    </>
  );
}

function FieldEdit({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">{label}</div>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function bumpPatch(v: string): string {
  const m = v.match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!m) return v + ".1";
  return `${m[1]}.${m[2]}.${Number(m[3]) + 1}`;
}

function RevokeModal({
  open,
  onClose,
  app,
  version,
}: {
  open: boolean;
  onClose: () => void;
  app: Application;
  version: string;
}) {
  const push = useToast();
  const qc = useQueryClient();
  const [reason, setReason] = useState("");

  const revokeMut = useMutation({
    mutationFn: () =>
      revokeVersion(app.pkg, version, { reason: reason.trim() || null }),
    onSuccess: async (ver) => {
      await qc.invalidateQueries({ queryKey: ["app", app.pkg] });
      push({ kind: "warn", text: `Версия ${ver.version} отозвана` });
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Не удалось отозвать версию") : "Не удалось отозвать версию";
      push({ kind: "warn", text: msg });
    },
  });

  return (
    <Modal
      open={open}
      onClose={revokeMut.isPending ? () => {} : onClose}
      title={<span className="flex items-center gap-2 text-[var(--color-danger)]"><ShieldAlert size={16} />Отзыв версии</span>}
      subtitle={`${app.name} · ${version}`}
      size="sm"
      footer={
        <>
          <Btn onClick={onClose}>Отмена</Btn>
          <Btn variant="primary" onClick={() => revokeMut.mutate()}>
            {revokeMut.isPending ? "Отзыв…" : "Отозвать"}
          </Btn>
        </>
      }
    >
      <div className="text-[13px] text-[var(--color-fg-muted)]">
        Версия будет помечена как отозванная в подписанном каталоге. Установленным устройствам
        отправится уведомление об обязательном обновлении.
      </div>
      <label className="mt-4 block text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
        Причина отзыва
      </label>
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={revokeMut.isPending}
        placeholder="CVE-2026-… или описание проблемы"
        className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-fg)] outline-none transition-colors focus:border-[var(--color-fg-muted)]"
      />
    </Modal>
  );
}

function NotFound({ pkg }: { pkg: string }) {
  return (
    <div className="grid place-items-center rounded-xl border border-dashed border-[var(--color-border-strong)] bg-[var(--color-surface)] px-6 py-16 text-center">
      <div className="text-[14px] font-semibold text-[var(--color-fg)]">Приложение не найдено</div>
      <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
        Пакет <span className="font-mono">{pkg}</span> отсутствует в каталоге.
      </div>
      <Link to="/admin/apps" className="mt-4 text-[13px] text-[var(--color-brand)] hover:underline">
        ← Вернуться к каталогу
      </Link>
    </div>
  );
}
